import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { APPS, CATEGORIES, GUIDE_CLUSTERS } from '../data/homeApps';
import SCREENS from '../data/homeScreens.json';
import PandaMarkLive from '../components/brand/PandaMarkLive';

// The PandaTaps homepage.
//
// Structure taken from beside.com, measured off the real site rather than
// guessed: a centred axis, large type at weight 400 with tight tracking,
// near-monochrome chrome, one idea per screen, and a sticky pill bar that
// stays put while the content under it swaps.
//
// The one departure, forced by our assets: Beside shows raw product UI, so its
// panels can be bare. Our App Store screenshots are COMPOSED - each already
// carries its own headline, device frame, palette and wordmark. Dropping those
// into a bare panel would give a frame inside a frame and two headlines
// fighting. So the page supplies almost no framing of its own: the screenshot
// runs large and uncropped, and the copy beside it stays deliberately quiet.
//
// Screenshots are pulled from App Store Connect and live in public/screens/,
// two per app. src/data/homeScreens.json is the manifest and the running order.
const ink = '#1E1D1C';
const mid = '#6B6A66';
// Was #9A9893, which measures 2.88:1 on paper and 2.67:1 on the sunk bands -
// under the 4.5:1 floor for the small uppercase text it was used on. #6B6A66
// clears it at 5.41:1 / 5.01:1; the size, tracking and uppercasing already
// carry the hierarchy without leaning on low contrast.
const faint = '#6B6A66';
const paper = '#FFFFFF';
const sunk = '#F7F6F3';
const line = '#E6E4DF';

// Navbar.js sets height: 68px. Hardcoding 71 here left a 3px gap between the
// nav and the sticky filter bar that content visibly scrolled through.
const NAV_H = 68;

const Page = styled.div`
  /* Tabbing backwards used to land focus underneath the fixed nav and the
     sticky bar. 68 + 60 + 2 clears both plus the ring. */
  a, button { scroll-margin-top: ${NAV_H + 62}px; }

  /* The horizontal pill scroller's 1,002px of content was escaping into the
     document's scrollable overflow, giving the whole page 617px of sideways
     scroll at 390px and stretching the fixed nav to 1,007px with it.
     clip rather than hidden on purpose: hidden on an ancestor silently
     kills position: sticky, which this page depends on. */
  overflow-x: clip;
  padding-top: ${NAV_H}px;
  background: ${paper};
  color: ${ink};
  min-height: 100vh;
  font-family: var(--font-body);
`;

const Container = styled.div`
  width: 92%;
  max-width: 1180px;
  margin: 0 auto;
`;

/* ---------------- hero ---------------- */
// Centred and mostly empty on purpose. The reference gives its first screen to
// one sentence and one object, and trusts the visitor to scroll.
const Hero = styled.section`
  text-align: center;
  padding: 72px 0 58px;
  @media (max-width: 768px) { padding: 48px 0 40px; }
`;

// The mark leads the page, above the kicker. At 96px it clears the 48px motion
// floor, so the face blinks, breathes, twitches an ear and follows the pointer
// - the one moment of warmth on an otherwise deliberately quiet page.
const HeroMark = styled.div`
  display: grid;
  place-items: center;
  width: 96px;
  height: 96px;
  margin: 0 auto 30px;
  svg { display: block; }
  @media (max-width: 768px) { width: 76px; height: 76px; margin-bottom: 22px; }
`;

const Kicker = styled.div`
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${faint};
  margin-bottom: 26px;
`;

const H1 = styled.h1`
  font-size: clamp(2.1rem, 5.4vw, 3.9rem);
  line-height: 1.06;
  letter-spacing: -0.042em;
  font-weight: 400;
  color: ${ink};
  /* The headline now stands alone - no lede under it - so this carries the
     whole gap down to the buttons. */
  margin: 0 auto 2.7rem;
  max-width: 17ch;
`;

const Lede = styled.p`
  font-size: 1.12rem;
  line-height: 1.62;
  color: ${mid};
  max-width: 52ch;
  margin: 0 auto 2.6rem;
`;

// No app count anywhere in the hero. How many there are is the studio's
// business, not a reason for anyone to stay - the apps themselves are.
const HeroActions = styled.div`
  display: flex;
  gap: 11px;
  justify-content: center;
  flex-wrap: wrap;
`;

// NOT an <a href="#..."> - ScrollManager owns scroll for the whole site and
// jumps to top on every history change, and a hash link creates one, so the
// anchor would be undone the instant it fired. Scrolling the element directly
// leaves history alone and works regardless of that.
const HeroBtn = styled.button`
  appearance: none;
  cursor: pointer;
  font-family: var(--font-body);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.88rem 1.6rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  min-height: 46px;
  border: 1px solid ${({ $solid }) => ($solid ? ink : line)};
  background: ${({ $solid }) => ($solid ? ink : paper)};
  color: ${({ $solid }) => ($solid ? '#fff' : ink)};
  transition: background 150ms ease, border-color 150ms ease, opacity 150ms ease;
  &:hover {
    opacity: ${({ $solid }) => ($solid ? 0.84 : 1)};
    background: ${({ $solid }) => ($solid ? ink : sunk)};
    border-color: ${({ $solid }) => ($solid ? ink : '#D6D4CE')};
  }
`;

/* ---------------- sticky category bar ---------------- */
// The Beside mechanic. It stays pinned and the work below it swaps.
const Sticky = styled.div`
  scroll-margin-top: ${NAV_H}px;
  position: sticky;
  top: ${NAV_H}px;
  z-index: 30;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: saturate(180%) blur(14px);
  border-top: 1px solid ${line};
  border-bottom: 1px solid ${line};
  padding: 11px 0;

  /* Retracts while you scroll down and comes back the moment you scroll up -
     on a 21,000px page a permanently pinned filter is just a band of chrome
     following you down, and on a phone it cost three rows of height. */
  transform: translateY(${({ $hidden }) => ($hidden ? '-110%' : '0')});
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  /* visibility, not just opacity: transparent buttons stay in the tab order,
     so a keyboard visitor would Tab into a bar they cannot see. Delayed on the
     way out so the slide still animates, instant on the way back in. */
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  transition: transform 260ms cubic-bezier(.4, 0, .2, 1), opacity 180ms ease,
              visibility 0s linear ${({ $hidden }) => ($hidden ? '260ms' : '0s')};
  will-change: transform;

  /* Anything inside reaching focus means the visitor tabbed here - show it. */
  &:focus-within {
    transform: none;
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: visibility 0s linear ${({ $hidden }) => ($hidden ? '0s' : '0s')};
  }
`;

// Seven pills wrapped onto three rows at phone width, which ate most of the
// screen. One scrollable row instead, with the edges faded so it reads as
// scrollable rather than clipped.
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

const Pills = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 780px) {
    flex-wrap: nowrap;
    justify-content: flex-start;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }
    scroll-snap-type: x proximity;
    padding-inline: 2px;
    /* Room for the focus ring inside the scroller without changing its height. */
    padding-block: 5px;
    margin-block: -5px;
    /* Trailing edge only. A leading fade greyed out the selected "Everything"
       pill in the state every visitor arrives in. */
    mask-image: linear-gradient(90deg, #000 0, #000 calc(100% - 16px),
                                transparent 100%);
    > * { scroll-snap-align: start; flex: 0 0 auto; }
  }
`;

const Pill = styled.button`
  appearance: none;
  cursor: pointer;
  border: 1px solid ${({ $on }) => ($on ? ink : 'transparent')};
  background: ${({ $on }) => ($on ? ink : 'transparent')};
  color: ${({ $on }) => ($on ? '#fff' : mid)};
  font-family: var(--font-body);
  font-size: 0.86rem;
  font-weight: 550;
  padding: 7px 15px;
  border-radius: 999px;
  min-height: 36px;
  transition: background 140ms ease, color 140ms ease;
  &:hover { background: ${({ $on }) => ($on ? ink : sunk)}; color: ${({ $on }) => ($on ? '#fff' : ink)}; }
  &:focus-visible { outline: 2px solid ${ink}; outline-offset: 2px; }
  /* Explicit colour per state. opacity: 0.55 put the count at 2.23:1. */
  span { color: ${({ $on }) => ($on ? 'rgba(255,255,255,0.82)' : '#6F6D69')};
         margin-left: 6px; font-size: 0.78em; }
`;

/* ---------------- one app per band ---------------- */
const Band = styled.section`
  background: ${({ $i }) => ($i % 2 ? sunk : paper)};
  border-bottom: 1px solid ${line};
  padding: 58px 0;
  @media (max-width: 880px) { padding: 46px 0; }
`;

// Narrower than the rest of the page on purpose: the copy only runs to 46ch, so
// a full 1180px bed leaves a river of dead space between the text and the phone.
const BandGrid = styled(Container)`
  max-width: 1000px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 72px;
  align-items: center;
  direction: ${({ $flip }) => ($flip ? 'rtl' : 'ltr')};
  > * { direction: ltr; }
  @media (max-width: 880px) {
    grid-template-columns: 1fr;
    gap: 34px;
    direction: ltr;
  }
`;

// The app's own icon, at the size it is drawn for - it is the one piece of
// brand the visitor will recognise again on the App Store. The old "03 ·
// APPX014" line was internal catalogue numbering and meant nothing here, so
// the slot now carries the category instead, which matches the sticky bar.
const Ident = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 22px;
`;

const Icon = styled.img`
  width: 54px;
  height: 54px;
  border-radius: 13px;
  display: block;
  flex-shrink: 0;
  border: 1px solid ${line};
  background: ${paper};
`;

const Cat = styled.div`
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${faint};
  display: flex;
  align-items: center;
  gap: 9px;
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $a }) => $a};
  display: inline-block;
  flex-shrink: 0;
`;

const Name = styled.h2`
  font-size: clamp(1.7rem, 3.2vw, 2.5rem);
  line-height: 1.08;
  letter-spacing: -0.035em;
  font-weight: 400;
  color: ${ink};
  margin: 0 0 0.6rem;
`;

const Tag = styled.div`
  font-size: 1.08rem;
  color: ${mid};
  margin-bottom: 1.3rem;
`;

const Desc = styled.p`
  font-size: 1rem;
  line-height: 1.68;
  color: ${mid};
  margin: 0 0 1.9rem;
  max-width: 46ch;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

// Real store buttons, not a status label. "On the App Store" was a dead green
// badge - it told you the app existed and then made you go through the product
// page to act on it. These go straight to the listing.
//
// Apple and Google both require their own wordmark and glyph on a download
// control, so the marks are drawn here rather than approximated with text.
const StoreRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const StoreBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  background: ${ink};
  color: #fff;
  border-radius: 10px;
  padding: 0.5rem 0.95rem;
  min-height: 46px;
  text-decoration: none;
  transition: opacity 140ms ease;
  &:hover { opacity: 0.84; }
  &:focus-visible { outline: 2px solid ${ink}; outline-offset: 2px; }
  svg { flex-shrink: 0; }
  .l { display: flex; flex-direction: column; line-height: 1.1; text-align: left; }
  .l small { font-size: 0.6rem; letter-spacing: 0.04em; opacity: 0.82; }
  .l b { font-size: 0.92rem; font-weight: 600; letter-spacing: -0.01em; }
`;

// Apple's glyph, and Google Play's four-colour triangle.
const AppleGlyph = () => (
  <svg width="19" height="22" viewBox="0 0 814 1000" fill="currentColor" aria-hidden="true">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
  </svg>
);

const PlayGlyph = () => (
  <svg width="19" height="21" viewBox="0 0 512 512" aria-hidden="true">
    <path d="M47 18 318 256 47 494c-9-6-15-17-15-31V49c0-14 6-25 15-31z" fill="#00D4FF" />
    <path d="M318 256 406 174l66 59c17 15 17 31 0 46l-66 59z" fill="#FFCE00" />
    <path d="M47 494l271-238 88 82-294 169c-23 13-47 2-65-13z" fill="#00F076" />
    <path d="M47 18l271 238 88-82L112 5C89-8 65 3 47 18z" fill="#FF3A44" />
  </svg>
);

const Secondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.92rem;
  font-weight: 600;
  color: ${ink};
  text-decoration: none;
  padding: 0.7rem 1.1rem;
  min-height: 46px;
  border: 1px solid ${line};
  border-radius: 10px;
  transition: border-color 140ms ease, background 140ms ease;
  &:hover { background: ${sunk}; border-color: #D6D4CE; }
  &:focus-visible { outline: 2px solid ${ink}; outline-offset: 2px; }
`;

const Ghost = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $live }) => ($live ? '#1F6F4A' : faint)};
  background: ${({ $live }) => ($live ? '#E8F2EC' : sunk)};
  border-radius: 6px;
  padding: 7px 10px;
`;

// The screenshot is the hero image and it is already art-directed. No device
// mockup, no gradient, no drop shadow stack - anything added here competes with
// the composition that is already inside the frame.
// Constrained by HEIGHT, not width. These are 1290x2796 - a 1:2.17 portrait -
// so sizing by width makes every band taller than the viewport and the page
// runs to 28,000px. Capping the height keeps one app to roughly one screen,
// which is the whole point of the reference.
const Shot = styled.div`
  display: flex;
  justify-content: center;
  img {
    max-height: 620px;
    max-width: 100%;
    width: auto;
    height: auto;
    display: block;
    border-radius: 18px;
    border: 1px solid ${line};
  }
  @media (max-width: 880px) {
    img { max-height: 520px; }
  }
`;

/* ---------------- the quiet index ---------------- */
// Everything, including the two apps that have no screenshot yet, so the page
// never implies the portfolio is only what it just showed.
const Index = styled.section`
  padding: 76px 0 70px;
  background: ${paper};
`;

const IndexHead = styled.div`
  text-align: center;
  margin-bottom: 34px;
`;

const H2c = styled.h2`
  font-size: clamp(1.5rem, 2.8vw, 2.1rem);
  font-weight: 400;
  letter-spacing: -0.032em;
  margin: 0 0 10px;
  color: ${ink};
`;

const Grid = styled.div`
  display: grid;
  /* minmax(0, ...) - a bare 1fr floors at min-content, and the nowrap app
     names pushed the track wider than its share, clipping the last column
     against the container's overflow: hidden. */
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  background: ${line};
  border: 1px solid ${line};
  border-radius: 14px;
  overflow: hidden;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 520px) { grid-template-columns: minmax(0, 1fr); }
`;

const Cell = styled(Link)`
  background: ${paper};
  padding: 15px 16px 16px;
  text-decoration: none;
  display: flex;
  gap: 11px;
  align-items: center;
  transition: background 130ms ease;
  &:hover { background: ${sunk}; }
  /* Negative offset draws the ring inside the cell - the grid clips it. */
  &:focus-visible { outline: 2px solid ${ink}; outline-offset: -3px; }
  img { width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0; }
  .t { min-width: 0; }
  .n { font-size: 0.88rem; font-weight: 600; color: ${ink}; line-height: 1.28;
       overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .s { font-family: var(--font-mono); font-size: 9px; letter-spacing: 0.12em;
       text-transform: uppercase; color: ${faint}; margin-top: 3px; }
`;

/* ---------------- guides ---------------- */
const Guides = styled.section`
  scroll-margin-top: ${NAV_H + 40}px;
  background: ${sunk};
  border-top: 1px solid ${line};
  padding: 70px 0;
`;

const GGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const GCard = styled(Link)`
  background: ${paper};
  border: 1px solid ${line};
  border-radius: 12px;
  padding: 17px 16px 19px;
  text-decoration: none;
  transition: border-color 140ms ease;
  &:hover { border-color: ${ink}; }
  .l { font-size: 1rem; font-weight: 600; color: ${ink}; }
  .n { font-size: 0.85rem; color: ${mid}; margin-top: 5px; line-height: 1.45; }
`;

// screens.json is ordered: Sterling, Bowling, Volleyball, Needlepoint, then the
// rest. Join it to the live app records so copy and palette come from one place.
// scroll-margin-top on the targets clears the fixed nav.
//
// Smooth is right for a short hop and wrong for a long one. The guides sit
// about 13,700px down, past 26 app bands: smooth-scrolling that took ~2s and
// dragged fifteen viewports of half-loaded screenshots through the window,
// which reads as the page lurching rather than as navigation. Beyond a couple
// of viewports a browser's own anchor behaviour is instant, so match it.
const SMOOTH_WITHIN_VIEWPORTS = 2.5;

// Programmatic scrolls must not be read as "the visitor scrolled down", or the
// filter bar retracts the instant you arrive at it. Set while a jump is in
// flight and cleared once the scroll settles.
let programmaticUntil = 0;
export const isProgrammaticScroll = () => Date.now() < programmaticUntil;

const jumpTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const far =
    Math.abs(el.getBoundingClientRect().top) >
    window.innerHeight * SMOOTH_WITHIN_VIEWPORTS;
  const reduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const instant = far || reduced;
  programmaticUntil = Date.now() + (instant ? 300 : 900);
  el.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'start' });

  // scrollIntoView moves the viewport but, unlike a real <a href="#id">, leaves
  // the sequential focus navigation point behind. Without this the next Tab
  // sends a keyboard visitor back to the top of a 21,000px document, and a
  // screen reader is told nothing at all.
  el.setAttribute('tabindex', '-1');
  el.focus({ preventScroll: true });
  el.addEventListener('blur', () => el.removeAttribute('tabindex'), { once: true });
};

const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));

// Hide the filter bar while scrolling down, bring it back on the way up.
// Threshold is 6px so a trackpad's jitter does not flicker it, and it never
// hides while you are still near the top where the bar has not yet pinned.
const useRetractOnScroll = (enabled = true) => {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const frame = useRef(0);
  // Where the bar actually pins. Retracting before that point hid an element
  // that was still in normal flow, leaving a blank strip behind it.
  const pinAt = useRef(Number.MAX_SAFE_INTEGER);

  useEffect(() => {
    if (!enabled) return undefined;
    lastY.current = window.scrollY;
    const anchor = document.getElementById('apps-top');
    if (anchor) {
      let flow = 0;
      for (let n = anchor; n; n = n.offsetParent) flow += n.offsetTop;
      pinAt.current = flow - NAV_H;
    }

    const read = () => {
      frame.current = 0;
      const y = window.scrollY;
      const dy = y - lastY.current;
      if (Math.abs(dy) > 6) {
        // A jump we started ourselves is not the visitor scrolling away.
        if (isProgrammaticScroll()) setHidden(false);
        else setHidden(dy > 0 && y > pinAt.current);
        lastY.current = y;
      }
    };
    const onScroll = () => {
      if (!frame.current) frame.current = window.requestAnimationFrame(read);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame.current) window.cancelAnimationFrame(frame.current);
    };
  }, [enabled]);

  const pin = useCallback(() => {
    setHidden(false);
    lastY.current = window.scrollY;
  }, []);

  return [hidden, pin];
};

const BY_URL = Object.fromEntries(APPS.map((a) => [a.url, a]));
const SHOWN = SCREENS.map((s) => ({ ...BY_URL[s.url], ...s })).filter((a) => a.name);

const HomePage = () => {
  const [cat, setCat] = useState('all');
  const [retracted, pinBar] = useRetractOnScroll();

  // Picking a category shortens the list under you, so without a correction the
  // browser keeps your old offset and clamps you into the footer - the apps you
  // just asked for end up above the viewport, unseen.
  //
  // This must measure a STATIC element. getBoundingClientRect() on the sticky
  // bar returns its PINNED box, so `scrollY + rect.top - NAV_H` collapses to
  // `scrollY` and the guard can never fire. #apps-top is a zero-height marker
  // in normal flow that sticky never moves.
  const pick = useCallback((id) => {
    const anchor = document.getElementById('apps-top');
    let top = null;
    if (anchor) {
      let flow = 0;
      for (let n = anchor; n; n = n.offsetParent) flow += n.offsetTop;
      top = flow - NAV_H;
    }
    setCat(id);
    if (top !== null && window.scrollY > top) {
      programmaticUntil = Date.now() + 300;
      window.scrollTo({ top, behavior: 'auto' });
    }
    pinBar();
  }, [pinBar]);
  const bands = useMemo(
    () => (cat === 'all' ? SHOWN : SHOWN.filter((a) => a.category === cat)),
    [cat]
  );

  return (
    <Page>
      <Hero>
        <Container>
          <HeroMark>
            <PandaMarkLive size={96} title="Panda Taps" ink={ink} />
          </HeroMark>
          <Kicker>PandaTaps &middot; Independent iOS studio &middot; Chicago</Kicker>
          <H1>Apps that earn their place on your home screen.</H1>
          <HeroActions>
            <HeroBtn type="button" $solid onClick={() => jumpTo('apps-top')}>
              Browse the apps
            </HeroBtn>
            <HeroBtn type="button" onClick={() => jumpTo('guides')}>
              Free reference guides
            </HeroBtn>
          </HeroActions>
        </Container>
      </Hero>

      {/* Static flow marker. The sticky bar cannot be measured reliably
          because its rect is its pinned box, so scroll maths uses this. */}
      <div id="apps-top" aria-hidden="true" style={{ position: 'relative', height: 0 }} />
      <Sticky $hidden={retracted}>
        <Container>
          {/* aria-pressed carries the selected state: without it the filter is
              just a row of unlabelled buttons to a screen reader, with no way
              to tell which one is active. The count needs its own word or it
              runs into the label as "Everything26". */}
          <Pills role="group" aria-label="Filter apps by category">
            <Pill type="button" aria-pressed={cat === 'all'} $on={cat === 'all'}
                  onClick={() => pick('all')}>
              Everything<span aria-hidden="true">{SHOWN.length}</span>
              <VisuallyHidden>, {SHOWN.length} apps</VisuallyHidden>
            </Pill>
            {CATEGORIES.map((c) => {
              const n = SHOWN.filter((a) => a.category === c.id).length;
              if (!n) return null;
              return (
                <Pill key={c.id} type="button" aria-pressed={cat === c.id} $on={cat === c.id}
                      onClick={() => pick(c.id)}>
                  {c.label}<span aria-hidden="true">{n}</span>
                  <VisuallyHidden>, {n} apps</VisuallyHidden>
                </Pill>
              );
            })}
          </Pills>
          {/* Filtering changes the page silently otherwise. */}
          <VisuallyHidden role="status" aria-live="polite">
            {cat === 'all'
              ? `Showing all ${bands.length} apps`
              : `Showing ${bands.length} ${CAT_LABEL[cat]} app${bands.length === 1 ? '' : 's'}`}
          </VisuallyHidden>
        </Container>
      </Sticky>

      {bands.map((a, i) => (
        <Band key={a.url} $i={i}>
          <BandGrid $flip={i % 2 === 1}>
            <div>
              <Ident>
                <Icon src={a.icon} alt="" width="54" height="54" />
                <Cat>
                  <Dot $a={a.accent} />
                  {CAT_LABEL[a.category] || 'App'}
                </Cat>
              </Ident>
              <Name>{a.name}</Name>
              <Tag>{a.tagline}</Tag>
              <Desc>{a.description}</Desc>
              <Actions>
                <StoreRow>
                  {a.appStoreUrl && (
                    <StoreBtn href={a.appStoreUrl} target="_blank" rel="noopener noreferrer">
                      <AppleGlyph />
                      <span className="l">
                        <small>Download on the</small>
                        <b>App Store</b>
                      </span>
                    </StoreBtn>
                  )}
                  {a.playStoreUrl && (
                    <StoreBtn href={a.playStoreUrl} target="_blank" rel="noopener noreferrer">
                      <PlayGlyph />
                      <span className="l">
                        <small>Get it on</small>
                        <b>Google Play</b>
                      </span>
                    </StoreBtn>
                  )}
                  {!a.appStoreUrl && <Ghost $live={false}>Coming soon</Ghost>}
                  <Secondary to={a.url}>Learn more &rarr;</Secondary>
                </StoreRow>
              </Actions>
            </div>
            <Shot>
              <img
                src={`/screens/${a.dir}/01.jpg`}
                alt={`${a.name} on iPhone`}
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchPriority={i === 0 ? 'high' : 'auto'}
                decoding="async"
                width="756"
                height="1640"
              />
            </Shot>
          </BandGrid>
        </Band>
      ))}

      <Index>
        <Container>
          <IndexHead>
            <H2c>Every app, including the ones still in the oven.</H2c>
            {/* Deliberately says nothing about App Store submission state -
                that was inferred from whether WE had a screenshot, which is
                not the same thing and is not the visitor's concern. */}
            <Lede style={{ marginBottom: 0 }}>
              The ones still in progress are listed too, so nothing is hidden for
              want of a screenshot.
            </Lede>
          </IndexHead>
          <Grid>
            {APPS.map((a) => (
              <Cell key={a.url} to={a.url}>
                <img src={a.icon} alt="" />
                <div className="t">
                  <div className="n">{a.name}</div>
                  <div className="s">{a.live ? 'Live' : 'Soon'}</div>
                </div>
              </Cell>
            ))}
          </Grid>
        </Container>
      </Index>

      <Guides id="guides">
        <Container>
          <IndexHead>
            <H2c>Free reference guides</H2c>
            <Lede style={{ marginBottom: 0 }}>
              Around 190 pages of trade reference, written as references and free to
              everyone, app or no app.
            </Lede>
          </IndexHead>
          <GGrid>
            {GUIDE_CLUSTERS.map((g) => (
              <GCard key={g.to} to={g.to}>
                <div className="l">{g.label}</div>
                <div className="n">{g.note}</div>
              </GCard>
            ))}
          </GGrid>
        </Container>
      </Guides>
    </Page>
  );
};

export default HomePage;
